import { useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Chip,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { api } from '@/api';
import { nomeProprio } from '@/features/notas/texto';
import { useAsync } from '@/hooks/useAsync';

interface EventoAdmin {
  id: number;
  tipo: string;
  alvo: string;
  pagina: string | null;
  quando: string;
  ip: string | null;
  agente: string | null;
  conta: string | null;
  nome_google: string | null;
  ra: string | null;
  aluno: string | null;
  email_cps: string | null;
}

interface ResumoConta {
  conta: string | null;
  nome_google: string | null;
  aluno: string | null;
  ra: string | null;
  eventos: number;
  paginas: number;
  cliques: number;
  primeiro: string;
  ultimo: string;
}

const dataHora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
const semAcento = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

const COR_TIPO: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'default'> = {
  login: 'success',
  vinculo: 'warning',
  pagina: 'primary',
  clique: 'default',
};

/** Quem entrou, quando, e o que abriu e clicou. */
export function AcessosAba() {
  const { data, error, loading, reload } = useAsync(
    (signal) =>
      api.get<{ eventos: EventoAdmin[]; resumo: ResumoConta[] }>('/admin/acessos', {
        signal,
        query: { limite: 3000 },
      }),
    [],
  );
  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState('todos');

  const eventos = useMemo(() => {
    const q = semAcento(busca.trim());
    return (data?.eventos ?? []).filter(
      (e) =>
        (tipo === 'todos' || e.tipo === tipo) &&
        (!q ||
          semAcento(
            [e.aluno, e.nome_google, e.conta, e.ra, e.alvo, e.pagina].filter(Boolean).join(' '),
          ).includes(q)),
    );
  }, [data, busca, tipo]);

  if (error) return <Alert severity="error">Não consegui carregar os acessos.</Alert>;

  return (
    <Stack spacing={3}>
      <Card>
        <TableContainer sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Quem</TableCell>
                <TableCell align="right">Páginas</TableCell>
                <TableCell align="right">Cliques</TableCell>
                <TableCell>Primeiro acesso</TableCell>
                <TableCell>Último acesso</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.resumo ?? []).map((r) => (
                <TableRow
                  key={r.conta ?? '?'}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setBusca(r.conta ?? '')}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.aluno ? nomeProprio(r.aluno) : (r.nome_google ?? 'sem nome')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.conta}
                      {r.ra ? ` · RA ${r.ra}` : ' · RA não confirmado'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{r.paginas}</TableCell>
                  <TableCell align="right">{r.cliques}</TableCell>
                  <TableCell>{dataHora(r.primeiro)}</TableCell>
                  <TableCell>{dataHora(r.ultimo)}</TableCell>
                </TableRow>
              ))}
              {!loading && !data?.resumo.length && (
                <TableRow>
                  <TableCell colSpan={5}>Ninguém acessou ainda.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ alignItems: { md: 'center' } }}
      >
        <TextField
          size="small"
          placeholder="Filtrar por aluno, e-mail, RA ou página"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ minWidth: 320 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded />
                </InputAdornment>
              ),
            },
          }}
        />
        <ToggleButtonGroup size="small" exclusive value={tipo} onChange={(_, v) => v && setTipo(v)}>
          <ToggleButton value="todos">Tudo</ToggleButton>
          <ToggleButton value="login">Logins</ToggleButton>
          <ToggleButton value="pagina">Páginas</ToggleButton>
          <ToggleButton value="clique">Cliques</ToggleButton>
          <ToggleButton value="vinculo">Vínculo</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          {eventos.length} registros ·{' '}
          <Typography
            component="span"
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={reload}
          >
            atualizar
          </Typography>
        </Typography>
      </Stack>

      <Card>
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Quando</TableCell>
                <TableCell>Quem</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>O quê</TableCell>
                <TableCell>Página</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {eventos.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{dataHora(e.quando)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {e.aluno ? nomeProprio(e.aluno) : (e.nome_google ?? '–')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {e.conta}
                      {e.email_cps ? ` · ${e.email_cps}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={COR_TIPO[e.tipo] ?? 'default'}
                      label={e.tipo}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360, overflowWrap: 'anywhere' }}>{e.alvo}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}>
                    {e.pagina ?? ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Stack>
  );
}
